import { useState, useEffect } from "react";
import { Container, Card } from "~/components/ui";
import {
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  AdminPanelSettings,
  Close,
  UploadFile,
  TrendingUp,
  People,
  PointOfSale,
  AttachMoney,
  PersonAdd,
  Loyalty,
  Repeat,
} from "@mui/icons-material";
import { BulkTransactionImport } from "~/components/BulkTransactionImport";
import { KPICard } from "~/components/admin/KPICard";
import { RedemptionGauge } from "~/components/admin/RedemptionGauge";
import { RepeatPurchaseRateCard } from "~/components/admin/RepeatPurchaseRateCard";
import { TrendChart } from "~/components/admin/TrendChart";

interface DashboardData {
  period: {
    startDate: string;
    endDate: string;
  };
  financial: {
    totalSalesMTD: number;
    totalSalesLifetime: number;
    averageOrderValue: number;
    pointsLiability: number;
    revenuePerActiveMember: number;
    avgTransactionFrequency: number;
  };
  engagement: {
    totalMembers: number;
    activeMembers30d: number;
    activityRate: number;
    newMembers7d: number;
    redemptionRate: number;
    avgTimeToFirstRedemption: number;
    repeatPurchaseRates: {
      weekly: number;
      monthly: number;
      quarterly: number;
    };
  };
  charts: {
    salesTrend: Array<{ date: string; amount: number }>;
    aovTrend: Array<{ date: string; aov: number }>;
    activeMembersTrend: Array<{ date: string; count: number }>;
    memberAcquisition30d: Array<{ date: string; count: number }>;
  };
  alerts: Array<{
    severity: "warning" | "error";
    message: string;
  }>;
}

export default function Admin() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password === "442001") {
      setIsAuthenticated(true);
      setError("");
      // Load dashboard data
      loadDashboardData();
    } else {
      setError("كلمة المرور غير صحيحة");
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/admin/dashboard-full");
      if (!response.ok) {
        throw new Error("Failed to load dashboard data");
      }
      const data = await response.json();
      setDashboardData(data);
    } catch (err: any) {
      console.error("Dashboard load error:", err);
      setError("فشل في تحميل بيانات لوحة التحكم");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Container size="sm" padding>
        <Card padding="lg">
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <AdminPanelSettings sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              لوحة التحكم الإدارية
            </Typography>
            <Typography variant="body2" color="text.secondary">
              يرجى إدخال كلمة المرور للوصول
            </Typography>
          </Box>

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              type="password"
              label="كلمة المرور"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              autoFocus
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button fullWidth type="submit" variant="contained" size="large" sx={{ mt: 3 }}>
              دخول
            </Button>
          </form>
        </Card>
      </Container>
    );
  }

  if (loading || !dashboardData) {
    return (
      <Container size="xl" padding>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  const { financial, engagement, charts, alerts } = dashboardData;

  return (
    <Container size="xl" padding>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h3" component="h1">
          لوحة التحكم الإدارية
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={() => setOpenImportDialog(true)} startIcon={<UploadFile />}>
            استيراد معاملات
          </Button>
          <Button variant="outlined" onClick={loadDashboardData}>
            تحديث البيانات
          </Button>
        </Box>
      </Box>

{/* Alerts Section */}
      {alerts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          {alerts.map((alert, index) => (
            <Alert key={index} severity={alert.severity} sx={{ mb: 1 }}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* SECTION 1: FINANCIAL METRICS (6 KPIs) */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
        📊 المقاييس المالية
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="إجمالي المبيعات (الشهر الحالي)"
            value={financial.totalSalesMTD}
            unit="ج.م"
            icon={<AttachMoney />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="إجمالي الإيرادات (منذ البداية)"
            value={financial.totalSalesLifetime}
            unit="ج.م"
            icon={<PointOfSale />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="متوسط قيمة الطلب"
            value={financial.averageOrderValue}
            unit="ج.م"
            icon={<TrendingUp />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="النقاط المستحقة"
            value={financial.pointsLiability}
            unit="ج.م"
            alert={financial.pointsLiability > 2000 ? "warning" : undefined}
            subtitle={financial.pointsLiability > 2000 ? "تجاوزت الحد المسموح" : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="الإيراد لكل عضو نشط"
            value={financial.revenuePerActiveMember}
            unit="ج.م"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="متوسط عدد المعاملات لكل عميل"
            value={financial.avgTransactionFrequency}
            subtitle="معدل التكرار"
          />
        </Grid>
      </Grid>

      {/* Financial Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <TrendChart
            title="اتجاه المبيعات (تراكمي - الشهر الحالي)"
            data={charts.salesTrend.map((d) => ({ date: d.date, value: d.amount }))}
            valueLabel="المبيعات (ج.م)"
            type="area"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TrendChart
            title="متوسط قيمة الطلب (متوسط متحرك 7 أيام)"
            data={charts.aovTrend.map((d) => ({ date: d.date, value: d.aov }))}
            valueLabel="متوسط القيمة (ج.م)"
            type="line"
          />
        </Grid>
      </Grid>

      {/* SECTION 2: ENGAGEMENT METRICS (6 KPIs) */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
        👥 مقاييس التفاعل والولاء
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="إجمالي الأعضاء"
            value={engagement.totalMembers}
            icon={<People />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="الأعضاء النشطون (30 يوم)"
            value={engagement.activeMembers30d}
            icon={<People />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="معدل النشاط"
            value={engagement.activityRate}
            unit="%"
            alert={engagement.activityRate < 30 ? "warning" : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="أعضاء جدد (7 أيام)"
            value={engagement.newMembers7d}
            icon={<PersonAdd />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <RedemptionGauge rate={engagement.redemptionRate} />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <KPICard
            title="متوسط الوقت للاسترداد الأول"
            value={engagement.avgTimeToFirstRedemption}
            unit="يوم"
            icon={<Loyalty />}
            subtitle="من التسجيل حتى أول عملية استرداد"
          />
        </Grid>
      </Grid>

      {/* Repeat Purchase Rate */}
      <Box sx={{ mb: 4 }}>
        <RepeatPurchaseRateCard rates={engagement.repeatPurchaseRates} />
      </Box>

      {/* Engagement Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <TrendChart
            title="اتجاه الأعضاء النشطين (30 يوم)"
            data={charts.activeMembersTrend.map((d) => ({ date: d.date, value: d.count }))}
            valueLabel="عدد الأعضاء النشطين"
            type="line"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TrendChart
            title="اكتساب الأعضاء الجدد (30 يوم)"
            data={charts.memberAcquisition30d.map((d) => ({ date: d.date, value: d.count }))}
            valueLabel="عدد الأعضاء الجدد"
            type="area"
          />
        </Grid>
      </Grid>

      {/* Bulk Import Dialog */}
      <Dialog open={openImportDialog} onClose={() => setOpenImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          استيراد معاملات
          <IconButton onClick={() => setOpenImportDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BulkTransactionImport />
        </DialogContent>
      </Dialog>
    </Container>
  );
}
